/**
 * RightPanel — sliding auth + post-auth panel.
 *
 * Contains the Amplify Authenticator and all custom auth-service handlers.
 * Phase 4 will wire this into App.js as the sole render surface; until then
 * the file exists independently and is not imported.
 */

import { useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import {
  withCrossTabLock,
  broadcastAuthEvent,
  getCookieDomain,
  getCurrentAuthenticatedUserDeduped
} from "../utils";
import "@aws-amplify/ui-react/styles.css";
import "./RightPanel.css";

// ---------------------------------------------------------------------------
// Auth debug header (shown when ?debugAuth is in the URL)
// Dev-only AND localhost-only (OWASP A01/A09) — never visible on a deployed host.
// ---------------------------------------------------------------------------

const _isLocalDevHost = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
const shouldShowAuthDebug = process.env.NODE_ENV !== "production"
  && _isLocalDevHost
  && new URLSearchParams(window.location.search).has("debugAuth");

function AuthDebugHeader() {
  const { route, user, error, isPending } = useAuthenticator((context) => [
    context.route,
    context.user,
    context.error,
    context.isPending
  ]);

  const message =
    typeof error === "string"
      ? error
      : error?.message
        ? String(error.message)
        : error
          ? String(error)
          : "";

  return (
    <div style={{ padding: "0.5rem 0.75rem", fontSize: "12px", opacity: 0.8 }}>
      <div>
        auth.route: <strong>{String(route)}</strong>
        {isPending ? " (pending)" : ""}
      </div>
      <div>
        challenge: <strong>{String(user?.challengeName || "-")}</strong>
      </div>
      {message ? (
        <div style={{ marginTop: "0.25rem" }}>error: {message}</div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auth submission guards — rate-limiting, cross-tab locking, error handling
// ---------------------------------------------------------------------------

// Per-tab last-attempt timestamps for client-side rate limiting.
// Cross-tab mutual exclusion is delegated to `withCrossTabLock` (web-locks API,
// auto-released on tab crash) — see utils/index.js.
const submitState = {
  lastAttemptAt: new Map()
};

const MIN_SUBMIT_INTERVAL_MS = 1500;

// ---------------------------------------------------------------------------
// Session helpers
// ---------------------------------------------------------------------------

// getCurrentAuthenticatedUserDeduped is imported from ../utils (single source).

const isCognitoSessionExpiredError = (error) => {
  const name = error?.name || "";
  const message = String(error?.message || "").toLowerCase();

  if (
    name === "AuthSessionInvalidError" ||
    name === "UserUnAuthenticatedException"
  ) {
    return true;
  }

  if (name === "NotAuthorizedException") {
    return (
      message.includes("session") ||
      message.includes("expired") ||
      message.includes("invalid session") ||
      message.includes("refresh token")
    );
  }

  if (name === "InvalidParameterException") {
    return (
      message.includes("session") ||
      message.includes("challenge") ||
      message.includes("refresh token")
    );
  }

  if (
    message.includes("invalid refresh token") ||
    message.includes("refresh token")
  ) {
    return true;
  }

  return false;
};

const isCodeMismatchError = (error) =>
  error?.name === "CodeMismatchException" ||
  String(error?.message || "")
    .toLowerCase()
    .includes("code mismatch");

const isExpiredCodeError = (error) =>
  error?.name === "ExpiredCodeException" ||
  String(error?.message || "")
    .toLowerCase()
    .includes("code has expired");

const isNetworkError = (error) => {
  const name = String(error?.name || "");
  const message = String(error?.message || "").toLowerCase();
  return (
    name === "NetworkError" ||
    message.includes("network connection was lost") ||
    message.includes("failed to fetch") ||
    message.includes("network request failed") ||
    (!navigator.onLine && (name === "TypeError" || name === "Error"))
  );
};

const hasValidCognitoSession = (user) => {
  const session = user?.signInUserSession;
  if (!session) return false;

  if (typeof session.isValid === "function") {
    try {
      return Boolean(session.isValid());
    } catch {
      return false;
    }
  }

  return Boolean(session?.accessToken?.jwtToken);
};

// ---------------------------------------------------------------------------
// Cookie domain helper (used by GlobalSignOutHandler for cookie wipe)
// ---------------------------------------------------------------------------

// getCookieDomain is imported from ../utils (single source for App.js + here).

// ---------------------------------------------------------------------------
// Global sign-out — scoped cleanup of auth state only
// (Addendum 3 S5: do NOT wipe sibling-app cookies / localStorage / timers)
// ---------------------------------------------------------------------------

// Prefixes of localStorage / sessionStorage keys that are safe to wipe at
// sign-out. Anything not matching is left alone so sibling apps and our own
// non-auth state survive logout.
const AUTH_STORAGE_PREFIXES = [
  "CognitoIdentityServiceProvider.",
  "amplify-",
  "aws-amplify-",
  "auth:"
];

const wipeAuthStorage = (storage) => {
  try {
    const toRemove = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && AUTH_STORAGE_PREFIXES.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => storage.removeItem(k));
  } catch {
    /* ignore */
  }
};

// Cookie name prefixes considered auth cookies (Cognito + Amplify Hosted UI).
const AUTH_COOKIE_PREFIXES = [
  "CognitoIdentityServiceProvider.",
  "amplify-",
  "aws-amplify-",
  "amplify-signin-with-hostedUI",
  "amplify-redirected-from-hosted-ui"
];

const GlobalSignOutHandler = async (signOutProp) => {
  try {
    // S2: prefer a global sign-out (revokes refresh tokens at Cognito)
    // when the Authenticator's signOut prop is not supplied. When the
    // prop IS supplied we honor it (caller chose local-vs-global).
    const signOutPromise =
      typeof signOutProp === "function"
        ? signOutProp()
        : Auth.signOut({ global: true });

    await Promise.race([
      signOutPromise,
      new Promise((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(
                "Sign-out request timed out or network connection lost"
              )
            ),
          5000
        )
      )
    ]);
  } catch (e) {
    console.warn("Sign-out failed", e);
  } finally {
    try {
      // S5: scoped wipe instead of localStorage.clear() / sessionStorage.clear()
      wipeAuthStorage(window.localStorage);
      wipeAuthStorage(window.sessionStorage);
    } catch (e) {
      console.warn("Cleanup failed", e);
    }
    try {
      const cookieDomain = getCookieDomain();
      document.cookie.split(";").forEach((c) => {
        const cookieName = c.split("=")[0].trim();
        if (!cookieName) return;
        // S5: only clear auth cookies; leave sibling-app cookies untouched.
        if (!AUTH_COOKIE_PREFIXES.some((p) => cookieName.startsWith(p))) return;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${cookieDomain};Secure;SameSite=None`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;Secure;SameSite=None`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${cookieDomain}`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
      });
    } catch (e) {
      /* ignore */
    }

    // S3: tell other tabs to tear down before we navigate away.
    try {
      broadcastAuthEvent("SIGNED_OUT", { reason: "global-signout" });
    } catch {
      /* ignore */
    }

    // Force a hard reload to the login root to drop in-memory React state.
    window.location.replace("/");
  }
};

// ---------------------------------------------------------------------------
// withSubmitGuards — wraps every auth handler with rate-limit, cross-tab lock,
// expired-session detection, code-mismatch handling, and network-error recovery
// ---------------------------------------------------------------------------

const withSubmitGuards = (actionName, handler) =>
  async (...args) => {
    const now = Date.now();
    const lastAttemptAt = submitState.lastAttemptAt.get(actionName) || 0;

    if (now - lastAttemptAt < MIN_SUBMIT_INTERVAL_MS) {
      throw new Error("Please wait a moment before trying again.");
    }

    submitState.lastAttemptAt.set(actionName, now);

    // withCrossTabLock throws CrossTabLockBusy if another tab holds the lock;
    // releases automatically when the inner promise settles (or tab crashes).
    try {
      return await withCrossTabLock(actionName, () => handler(...args));
    } catch (error) {
      if (isNetworkError(error)) {
        throw new Error(
          "Network error — please check your connection and try again. " +
            "Your sign-in session is still active."
        );
      }

      if (isCognitoSessionExpiredError(error)) {
        GlobalSignOutHandler().catch(() => {});
        throw new Error(
          "Your verification session expired. Please sign in again and request a new code."
        );
      }

      if (isCodeMismatchError(error)) {
        throw new Error(
          "Invalid verification code. Use the most recent code and try again."
        );
      }

      if (isExpiredCodeError(error)) {
        throw new Error(
          "This verification code has expired. Please request a new code and try again."
        );
      }

      throw error;
    }
  };

// ---------------------------------------------------------------------------
// Individual auth handlers
// ---------------------------------------------------------------------------

const getUsername = (input = {}) =>
  input.username ||
  input.loginId ||
  input.email ||
  input.phone_number ||
  input?.user?.username;

const normalizeVerificationCode = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const buildConfirmSignInHandler = () =>
  withSubmitGuards("confirmSignIn", async (...args) => {
    let user;
    let code;
    let mfaType;

    if (args.length === 1 && typeof args[0] === "object" && args[0] !== null) {
      const input = args[0];

      user = input.user || input.signInUser || input.cognitoUser;

      const fv =
        (typeof input.formValues === "object" && input.formValues) || {};
      code =
        input.confirmation_code ??
        input.confirmationCode ??
        input.code ??
        fv.confirmation_code ??
        fv.confirmationCode ??
        fv.code ??
        null;

      if (code === null) {
        console.warn(
          "[confirmSignIn] Could not locate MFA code in handler input. " +
            "Expected input.confirmation_code or input.code. " +
            "Received keys:",
          Object.keys(input),
          "formValues keys:",
          Object.keys(fv)
        );
      }

      mfaType = input.mfaType || input.challengeName;
    } else if (
      args.length === 2 &&
      typeof args[0] === "object" &&
      args[0] !== null &&
      typeof args[1] === "object" &&
      args[1] !== null &&
      (args[0].user != null || args[1].data != null)
    ) {
      const context = args[0];
      const event = args[1];
      user = context.user;
      const data = (typeof event.data === "object" && event.data) || {};
      code =
        data.confirmation_code ??
        data.confirmationCode ??
        data.code ??
        null;
      mfaType = context.challengeName || context.user?.challengeName;
    } else {
      user = args[0];
      code = args[1] ?? null;
      mfaType = args[2];
    }

    code = normalizeVerificationCode(code);

    if (!user) {
      throw new Error(
        "Your sign-in session is missing. Please sign in again and request a new code."
      );
    }
    if (!code) {
      throw new Error("Confirmation code cannot be empty");
    }

    // Ensure mfaType is correctly populated from the user object if omitted
    mfaType = mfaType || user.challengeName || "SOFTWARE_TOKEN_MFA";

    const result = mfaType
      ? await Auth.confirmSignIn(user, code, mfaType)
      : await Auth.confirmSignIn(user, code);

    if (result?.challengeName) {
      return result;
    }

    try {
      return await Promise.race([
        getCurrentAuthenticatedUserDeduped(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Network timeout")), 8000)
        )
      ]);
    } catch {
      return result;
    }
  });

const signInHandler = withSubmitGuards("signIn", async (input = {}) => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("CognitoIdentityServiceProvider") ||
        key.startsWith("amplify-signin-with-hostedUI")
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch {}
  return Auth.signIn(getUsername(input), input.password);
});

const confirmSignInHandler = buildConfirmSignInHandler();

// Note: signUp / confirmSignUp / resendSignUp handlers intentionally NOT defined
// or overridden — Amplify defaults handle them, and overriding short-circuits the
// Hub autoSignIn event needed for the MFA_SETUP flow.

const forgotPasswordHandler = withSubmitGuards(
  "forgotPassword",
  async (input = {}) => {
    return Auth.forgotPassword(getUsername(input));
  }
);

const forgotPasswordSubmitHandler = withSubmitGuards(
  "forgotPasswordSubmit",
  async (input = {}) => {
    const username = getUsername(input);
    const code = normalizeVerificationCode(
      input.confirmationCode || input.code
    );
    const newPassword = input.newPassword || input.password;
    return Auth.forgotPasswordSubmit(username, code, newPassword);
  }
);

// ---------------------------------------------------------------------------
// authServices — passed to <Authenticator services={authServices}>
// ---------------------------------------------------------------------------

// getTotpSecretCode and verifyTotpToken are intentionally NOT overridden.
// Amplify UI v5's built-in implementations for these two are correct and must
// not be replaced — any custom override changes how the machine binds Auth
// calls during the incomplete challenge session and causes the SetupTOTP screen
// to error out silently.
const authServices = {
  signIn: signInHandler,
  confirmSignIn: confirmSignInHandler,
  // signUp intentionally omitted — Amplify default handles this.
  // confirmSignUp / resendSignUp intentionally omitted — overriding them
  // short-circuits the Hub autoSignIn event needed for MFA_SETUP flow.
  forgotPassword: forgotPasswordHandler,
  forgotPasswordSubmit: forgotPasswordSubmitHandler,
  // Backwards-compatible aliases expected by Amplify Authenticator internals
  handleSignIn: signInHandler,
  handleConfirmSignIn: confirmSignInHandler,
  handleForgotPassword: forgotPasswordHandler,
  handleForgotPasswordSubmit: forgotPasswordSubmitHandler
};

// ---------------------------------------------------------------------------
// Exported helpers — consumed by AuthenticatedContent (stays in App.js)
// ---------------------------------------------------------------------------

export {
  getCurrentAuthenticatedUserDeduped,
  isCognitoSessionExpiredError,
  hasValidCognitoSession,
  GlobalSignOutHandler
};

// ---------------------------------------------------------------------------
// RightPanel component
// ---------------------------------------------------------------------------

const ALLOWED_ORIGINS = [
  "http://127.0.0.1:8080",
  "http://localhost:3000",
  "http://localhost:8080",
  "https://development.tempclass.com",
  "https://tempclass.com",
  "https://tempcomputing.com",
  "https://www.tempcomputing.com"
];

// Resolve the parent's origin from document.referrer (only set when a parent
// frame navigated us here). Returns null when not embedded or unknown.
function resolveParentOrigin() {
  try {
    if (!document.referrer) return null;
    const ref = new URL(document.referrer);
    return `${ref.protocol}//${ref.host}`;
  } catch (e) {
    return null;
  }
}

function notifyParent(messageType, payload = {}) {
  try {
    if (window.self === window.parent) return; // Not inside an iframe
  } catch (e) {
    // Cross-origin throws an error. We are definitely inside an iframe!
  }

  // OWASP A01/A05: never use "*" target origin. Restrict to the parent's
  // origin (if it matches our allowlist) or fall through to the allowlist
  // entries. Browsers silently drop messages whose targetOrigin doesn't
  // match the receiver's own origin, so iterating the allowlist is safe.
  const parentOrigin = resolveParentOrigin();
  const targets = parentOrigin && ALLOWED_ORIGINS.includes(parentOrigin)
    ? [parentOrigin]
    : ALLOWED_ORIGINS;

  for (const target of targets) {
    try {
      window.parent.postMessage({ type: messageType, ...payload }, target);
    } catch (e) {
      // ignore individual delivery failures
    }
  }
}

function AuthPanelBridge() {
  const { route, toSignIn, toSignUp } = useAuthenticator((context) => [
    context.route,
    context.toSignIn,
    context.toSignUp
  ]);
  const hasReportedReadyRef = useRef(false);

  useEffect(() => {
    if (hasReportedReadyRef.current) {
      return;
    }

    notifyParent('AUTH_PANEL_READY', { route });
    hasReportedReadyRef.current = true;
  }, [route]);

  useEffect(() => {
    notifyParent('AUTH_PANEL_ROUTE_CHANGED', { route });
  }, [route]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!ALLOWED_ORIGINS.includes(event.origin)) {
        return;
      }

      if (event.data?.type !== 'AUTH_PANEL_NAVIGATE') {
        return;
      }

      if (event.data.route === 'signIn' && route !== 'signIn') {
        toSignIn();
      } else if (event.data.route === 'signUp' && route !== 'signUp') {
        toSignUp();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [route, toSignIn, toSignUp]);

  return null;
}

function InnerContent({ signOut, user, renderChildren }) {
  useEffect(() => {
    if (hasValidCognitoSession(user)) {
      notifyParent('AUTH_SUCCESS');
    }
  }, [user]);

  return renderChildren({ signOut, user });
}

export default function RightPanel({ children }) {
  const urlParams = new URLSearchParams(window.location.search);
  const routeParam = urlParams.get("route");
  const initialRoute = routeParam || "signIn";

  // Expose a global close function for sign-out and external triggers
  useEffect(() => {
    window.closeWorkflowPanel = () => notifyParent('CLOSE_AUTH_PANEL');
    return () => {
      delete window.closeWorkflowPanel;
    };
  }, []);

  return (
    <div className="right-panel">
      <Authenticator.Provider>
        <AuthPanelBridge />
        <Authenticator
          initialState={initialRoute}
          services={authServices}
          components={{
            Header: () => (
              <div style={{ textAlign: "center", padding: "1rem" }}>
                <a href="https://tempcomputing.com" target="_parent" style={{ color: "#377dff", textDecoration: "none", fontWeight: "500" }}>← Home</a>
              </div>
            ),
            ...(shouldShowAuthDebug ? { Footer: AuthDebugHeader } : {})
          }}
        >
          {({ signOut, user }) => (
            <InnerContent signOut={signOut} user={user} renderChildren={children} />
          )}
        </Authenticator>
      </Authenticator.Provider>
    </div>
  );
}
